/* eslint-disable no-unused-expressions */
import React, {useCallback, useEffect, useState} from "react";
import {
  CancelButton,
  CardContainer,
  Head,
  HeaderCard,
  SaveButton,
} from "../Styles";
import {Checkbox, Stack, styled} from "@mui/material";
import {LastDayMultiplier} from "./LastDayMultiplier";
import { Edit } from "@mui/icons-material";

const transformTableData = (tableData) => {
  return Object.keys(tableData || {}).map((timeKey) => ({
    time: timeKey,
    data: tableData[timeKey],
  }));
};

const EMPTY_SLOT = {
  id: null,
  minimumRooms: null,
  maximumRooms: null,
  multiplier: null,
  isDeleted: false,
};

const HeaderActions = React.memo(
  ({editMode, dayKey, setEditMode, handleCancel, onSubmit}) =>
    !editMode[dayKey] ? (
      <EditButton
        onClick={() => setEditMode((prev) => ({...prev,[dayKey]:true}))}
      >
        Edit
      </EditButton>
    ) : (
      <div>
        <CancelButton onClick={handleCancel} style={BUTTON_STYLES.cancelButton}>
          Cancel
        </CancelButton>
        <SaveButton onClick={onSubmit} style={BUTTON_STYLES.saveButton}>
          Save
        </SaveButton>
      </div>
    )
);

export const EnableDayMultiplier = ({
  title,
  checkboxKey,
  dayKey,
  apiResponseData = {},
  setIsCancelled,
  setIsSaved,
  handleMultiplier,
  multipliers = {},
  open = null,
  handleCloseStatus,
  networkMsg = "",
  updateLastDayData,
  loading,
  deleteTimeSlotRow,
  deleteRowInTimeSlot,
  editMode,
  setEditMode
}) => {
  const [weekDayData, setWeekDayData] = useState({});
  const [weekEndData, setWeekEndData] = useState({});

  useEffect(() => {
    setWeekDayData(apiResponseData?.[dayKey]?.weekday || {});
    setWeekEndData(apiResponseData?.[dayKey]?.weekend || {});
  }, [apiResponseData, dayKey]);

  const onSubmit = useCallback(async () => {
    const payloadData = [
      ...transformTableData(weekDayData),
      ...transformTableData(weekEndData),
    ];
    const status = await updateLastDayData(payloadData, dayKey);
    setIsSaved?.(true);
    if (status) setEditMode((prev) => ({...prev,[dayKey]:false}));
  }, [weekDayData, weekEndData, updateLastDayData, dayKey, setIsSaved, setEditMode]);

  const handleCancel = useCallback(() => {
    setIsCancelled?.(dayKey);
    setEditMode((prev) => ({...prev,[dayKey]:false}));
  }, [setIsCancelled, dayKey, setEditMode]);

  const changeHandler = useCallback(
    (setTableData, timeKey, slotIndex, field, value) => {
      setTableData((prevState) => {
        const newState = {...prevState};
        if (newState[timeKey] && newState[timeKey][slotIndex]) {
          newState[timeKey] = [...newState[timeKey]];
          newState[timeKey][slotIndex] = {
            ...newState[timeKey][slotIndex],
            [field]: value,
          };
        }
        return newState;
      });
    },
    []
  );

  const onRemoveRowHandler = useCallback((setTableData, timeKey, slotIndex) => {
    setTableData((prevState) => {
      const newState = {...prevState};
      if (newState[timeKey]) {
        newState[timeKey] = newState[timeKey].filter(
          (_, idx) => idx !== slotIndex
        );
        // If no slots left for this time, remove the time key
        if (newState[timeKey].length === 0) {
          delete newState[timeKey];
        }
      }
      return newState;
    });
  }, []);

  const onAddRowHandler = useCallback(
    (setTableData, applicableDay, isWeekDay, timeKey, slotIndex) => {
      const newSlot = {
        ...EMPTY_SLOT,
        applicableDay: applicableDay,
        isWeekDay: isWeekDay,
      };

      setTableData((prevState) => {
        const newState = {...prevState};
        if (newState[timeKey]) {
          newState[timeKey] = [
            ...newState[timeKey].slice(0, slotIndex + 1),
            newSlot,
            ...newState[timeKey].slice(slotIndex + 1),
          ];
        }
        return newState;
      });
    },
    []
  );

  const onAddTimeSlotHandler = useCallback(
    (setTableData, applicableDay, isWeekDay, timeKey = "--:--:--") => {
      const newSlot = {
        ...EMPTY_SLOT,
        applicableDay: applicableDay,
        isWeekDay: isWeekDay,
      };

      setTableData((prevState) => {
        const newState = {...prevState};

        newState[timeKey] = [newSlot];
        return newState;
      });
    },
    []
  );

  const onRemoveTimeSlotHandler = useCallback((setTableData, timeKey) => {
    setTableData((prevState) => {
      const newState = {...prevState};
      delete newState[timeKey];
      return newState;
    });
  }, []);

  const onTimeKeyChangeHandler = useCallback(
    (setTableData, oldTimeKey, newTimeValue) => {
      // Convert time input (HH:MM) to full time format (HH:MM:SS)

      const newTimeKey = newTimeValue.includes(":")
        ? newTimeValue.split(":").length === 2
          ? `${newTimeValue}:00`
          : newTimeValue
        : `${newTimeValue}:00:00`;

      if (oldTimeKey === newTimeKey) {
        return;
      }
      setTableData((prevState) => {
        const newState = {...prevState};

        // Move data from old time key to new time key
        if (newState[oldTimeKey]) {
          newState[newTimeKey] = [...newState[oldTimeKey]];
          delete newState[oldTimeKey];
        }

        return newState;
      });
    },
    []
  );

  const multiplierConfigs = [
    {
      title: "Weekday Setup",
      lastDayData: weekDayData,
      setTableData: setWeekDayData,
      isWeekDay: true,
    },
    {
      title: "Weekend Setup",
      lastDayData: weekEndData,
      setTableData: setWeekEndData,
      isWeekDay: false,
    },
  ];

  return (
    <CardContainer sx={{gridColumn: {xs: "span 3", xl: "span 3"}}}>
      <HeaderCard>
        <Head sx={{justifyContent: "flex-start"}}>
          <div className="title">{title}</div>
          <Checkbox
            checked={multipliers?.[checkboxKey] ?? false}
            onChange={(e) => handleMultiplier(checkboxKey, e.target.checked)}
            style={BUTTON_STYLES.checkbox}
          />
        </Head>
        <HeaderActions
          editMode={editMode}
          dayKey={dayKey}
          setEditMode={setEditMode}
          handleCancel={handleCancel}
          onSubmit={onSubmit}
        />
      </HeaderCard>

      <Stack direction={"row"} gap={5} width={"100%"}>
        {multiplierConfigs.map((config, index) => (
          <Stack key={index} alignItems="center" width="50%">
            <div className="title">{config.title}</div>
            <LastDayMultiplier
              lastDayData={config.lastDayData}
              setTableData={config.setTableData}
              applicableDay={dayKey}
              isWeekDay={config.isWeekDay}
              onChange={changeHandler}
              open={open}
              handleCloseStatus={handleCloseStatus}
              networkMsg={networkMsg}
              onRemoveRowHandler={onRemoveRowHandler}
              onAddRowHandler={onAddRowHandler}
              loading={loading}
              editMode={editMode[dayKey]}
              setEditMode={setEditMode}
              onAddTimeSlotHandler={onAddTimeSlotHandler}
              onTimeKeyChange={onTimeKeyChangeHandler}
              apiResponseData={apiResponseData}
              onRemoveTimeSlotHandler={onRemoveTimeSlotHandler}
              deleteTimeSlotRow={deleteTimeSlotRow}
              deleteRowInTimeSlot={deleteRowInTimeSlot}
            />
          </Stack>
        ))}
      </Stack>
    </CardContainer>
  );
};

const BUTTON_STYLES = {
  cancelButton: {margin: "5px 10px"},
  saveButton: {margin: "5px 10px", backgroundColor: "#306FBC"},
  checkbox: {color: "rgb(48,111,188)", width: "30px", height: "30px"},
  editButton: {margin: "5px 10px", backgroundColor: "#306FBC"},
};

const EditButton = styled(Edit)`
  margin-right:10px;
  height: 18px;
  width: 18px;
  color: #306fbc;
`;
